import { ReviewStars } from './ReviewStars';
import type { PublicReview } from './types';

export function formatReviewDate(iso: string) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

// Merchant reply block (doc 60 §4.2/4.3): rendered under the review it answers,
// muted and inset so it reads as the business's voice, never part of the guest's
// review. `businessName` is passed in by the surface (the API only carries the
// reply), so one component serves both the discover tab and the menu page.
export function MerchantReply({ reply, repliedAt, businessName }: { reply: string; repliedAt?: string | null; businessName?: string | null }) {
  return (
    <div className="mt-3 rounded-xl border border-[var(--dm-card-border)]/70 bg-[var(--color-bg-sunken)]/50 px-3 py-2.5">
      <p className="text-2xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
        {businessName ? `Response from ${businessName}` : 'Response'}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-[var(--color-text-secondary)]">{reply}</p>
      {repliedAt ? <p className="mt-1 text-2xs text-[var(--color-text-secondary)]/70">{formatReviewDate(repliedAt)}</p> : null}
    </div>
  );
}

// One public review row, shared by every surface that lists reviews. The
// verified chip is the difference between a rating and an assertion (a
// post-payment review is tied to a paid order), so it gets the visual weight.
export function ReviewListItem({ review, businessName }: { review: PublicReview; businessName?: string | null }) {
  return (
    <li className="py-4 first:pt-0">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-sm font-semibold">
            {review.displayName || 'Menuraq guest'}
          </p>
          {review.verifiedPurchase ? (
            <span className="shrink-0 rounded-full bg-[var(--color-success)]/10 px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide text-[var(--color-success)]">
              Verified order
            </span>
          ) : null}
        </div>
        <span className="shrink-0 text-2xs text-[var(--color-text-secondary)]">
          {formatReviewDate(review.createdAt)}
        </span>
      </div>
      <ReviewStars className="mt-1.5" value={review.rating} />
      <span className="sr-only">{review.rating} out of 5</span>
      {review.text ? (
        <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">{review.text}</p>
      ) : null}
      {review.merchantReply ? (
        <MerchantReply reply={review.merchantReply} repliedAt={review.merchantRepliedAt ?? null} businessName={businessName} />
      ) : null}
    </li>
  );
}
